/**
 * El cierre de la fiscalización de un servidor: culminar y sus memorandos.
 *
 * Culminar congela el resultado de cada documento en el memorando de
 * resultados (Anexo 04 de la directiva), AUNQUE queden respuestas en espera:
 * lo que llegue después va en un memorando complementario, uno por documento.
 * Los dos salen en PDF sin número y vuelven firmados del trámite documentario.
 */
import { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, StatusBadge } from '@mpfn/ui'
import { Callout, ConfirmDialog } from '@mpfn/patterns'
import { FileText, Flag } from 'lucide-react'
import {
  useCulminarExpediente,
  useGenerarComplementario,
  useMemorandos,
  useRegistrarMemorandoFirmado,
} from '../api/consultas.js'
import { urlDeArchivo, urlDeMemorando } from '../api/cliente.js'
import type { Memorando, ResultadoFiscalizacion, Tono } from '../api/tipos.js'
import { usePuede } from '../sesion/contexto.js'
import { avisarError, avisarExito } from '../lib/avisos.js'
import { fecha } from '../lib/formato.js'
import { Datos } from './Datos.js'
import { DialogoFirmado } from './DialogoFirmado.js'

const ETIQUETA_DE_RESULTADO: Readonly<Record<ResultadoFiscalizacion, string>> = {
  validado: 'Validado',
  no_validado: 'No validado',
  en_espera: 'En espera de respuesta',
  sin_respuesta: 'Sin respuesta',
  no_acreditado: 'No acreditado',
}

const TONO_DE_RESULTADO: Readonly<Record<ResultadoFiscalizacion, Tono>> = {
  validado: 'good',
  no_validado: 'critical',
  en_espera: 'info',
  sin_respuesta: 'warning',
  no_acreditado: 'serious',
}

export function FiscalizacionDelServidor({ expedienteId, nombre }: { expedienteId: string; nombre: string }) {
  const estado = useMemorandos(expedienteId)
  const puedeCulminar = usePuede('expediente.culminar')
  const culminar = useCulminarExpediente()
  const complementar = useGenerarComplementario()
  const [confirmando, setConfirmando] = useState(false)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resultado de la fiscalización</CardTitle>
      </CardHeader>
      <CardContent className="sigfisc-formulario">
        <Datos estado={estado}>
          {(datos) => {
            const resultados = datos.memorandos.find((memorando) => memorando.tipo === 'resultados')
            if (!resultados) {
              return (
                <>
                  <Callout
                    tone="info"
                    title="La fiscalización de este servidor sigue en curso"
                    {...(puedeCulminar
                      ? {
                          action: (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => {
                                setConfirmando(true)
                              }}
                            >
                              <Flag size={14} aria-hidden="true" />
                              Culminar la fiscalización
                            </Button>
                          ),
                        }
                      : {})}
                  >
                    Al culminar se emite el memorando de resultados con lo que diga cada documento hoy,
                    aunque alguno siga esperando respuesta: eso se comunicará después en un memorando
                    complementario. Cada documento necesita al menos un requerimiento.
                  </Callout>
                  <ConfirmDialog
                    open={confirmando}
                    onOpenChange={setConfirmando}
                    title="Culminar la fiscalización"
                    description={`Se emitirá el memorando de resultados de ${nombre} y ya no se podrán agregar documentos al expediente.`}
                    confirmLabel="Culminar y emitir el memorando"
                    loading={culminar.isPending}
                    onConfirm={() => {
                      culminar.mutate(expedienteId, {
                        onSuccess: () => {
                          avisarExito('Fiscalización culminada.', 'Descargue el memorando para numerarlo y firmarlo.')
                          setConfirmando(false)
                        },
                        onError: (error) => {
                          setConfirmando(false)
                          avisarError(error, 'No se pudo culminar la fiscalización')
                        },
                      })
                    }}
                  />
                </>
              )
            }

            const pendientes = datos.complementables.map((documentoId) => ({
              documentoId,
              denominacion:
                resultados.documentos?.find((item) => item.documentoId === documentoId)?.denominacion ?? 'Documento',
            }))

            return (
              <>
                <p className="sigfisc-nota">Culminada el {fecha(resultados.fecha)}.</p>
                {datos.memorandos.map((memorando) => (
                  <FilaMemorando key={memorando.id} memorando={memorando} />
                ))}
                {puedeCulminar &&
                  pendientes.map((pendiente) => (
                    <Callout
                      key={pendiente.documentoId}
                      tone="warning"
                      title={`Llegó la respuesta de «${pendiente.denominacion}»`}
                      action={
                        <Button
                          variant="primary"
                          size="sm"
                          loading={complementar.isPending && complementar.variables === pendiente.documentoId}
                          onClick={() => {
                            complementar.mutate(pendiente.documentoId, {
                              onSuccess: () => {
                                avisarExito('Memorando complementario emitido.')
                              },
                              onError: (error) => {
                                avisarError(error, 'No se pudo emitir el memorando complementario')
                              },
                            })
                          }}
                        >
                          Emitir el memorando complementario
                        </Button>
                      }
                    >
                      Estaba en espera al culminar: su resultado se comunica con un memorando complementario.
                    </Callout>
                  ))}
              </>
            )
          }}
        </Datos>
      </CardContent>
    </Card>
  )
}

function FilaMemorando({ memorando }: { memorando: Memorando }) {
  const puedeRegistrar = usePuede('expediente.culminar')
  const registrar = useRegistrarMemorandoFirmado()
  const [registrando, setRegistrando] = useState(false)
  const titulo =
    memorando.tipo === 'resultados'
      ? 'Memorando de resultados'
      : `Memorando complementario · «${memorando.documento?.denominacion ?? ''}»`

  return (
    <div className="sigfisc-memorando">
      <div className="sigfisc-memorando__cabecera">
        <strong>{titulo}</strong>
        <StatusBadge
          tone={memorando.evidenciaId ? 'good' : 'neutral'}
          label={memorando.numeroTramite ? `N.° ${memorando.numeroTramite}` : 'Sin numerar'}
        />
      </div>

      {memorando.documentos !== undefined && (
        <ul className="sigfisc-memorando__documentos">
          {memorando.documentos.map((documento) => (
            <li key={documento.documentoId}>
              <span>{documento.denominacion}</span>
              <StatusBadge tone={TONO_DE_RESULTADO[documento.resultado]} label={ETIQUETA_DE_RESULTADO[documento.resultado]} />
            </li>
          ))}
        </ul>
      )}
      {memorando.documento !== undefined && (
        <p className="sigfisc-nota">
          Comunica: {ETIQUETA_DE_RESULTADO[memorando.documento.resultado].toLowerCase()}.
        </p>
      )}

      <div className="sigfisc-acciones">
        {memorando.evidenciaId !== undefined ? (
          <Button variant="secondary" size="sm" asChild>
            <a href={urlDeArchivo(memorando.evidenciaId)} target="_blank" rel="noopener noreferrer">
              <FileText size={14} aria-hidden="true" />
              Ver el memorando firmado
            </a>
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" asChild>
              <a href={urlDeMemorando(memorando.id)} target="_blank" rel="noopener noreferrer">
                <FileText size={14} aria-hidden="true" />
                Descargar el memorando
              </a>
            </Button>
            {puedeRegistrar && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setRegistrando(true)
                }}
              >
                Registrar el memorando firmado
              </Button>
            )}
          </>
        )}
      </div>

      <DialogoFirmado
        abierto={registrando}
        onCambio={setRegistrando}
        titulo="Registrar el memorando firmado"
        descripcion={`${titulo}, del ${fecha(memorando.fecha)}.`}
        ejemploNumero="001631-2026-MP-FN-GG-GECPH-GEADPH-SGEADPH"
        etiquetaConfirmar="Registrar el memorando"
        cargando={registrar.isPending}
        onConfirmar={(firmado) => {
          registrar.mutate(
            { memorandoId: memorando.id, ...firmado },
            {
              onSuccess: () => {
                avisarExito(`Memorando N.° ${firmado.numero} registrado.`)
                setRegistrando(false)
              },
              onError: (error) => {
                avisarError(error, 'No se pudo registrar el memorando firmado')
              },
            },
          )
        }}
      />
    </div>
  )
}
